import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, MapPin, ArrowUp, Phone } from 'lucide-react';
import { Logo } from './ui/Logo';

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Rakshan001', label: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com/in/rakshan-shetty-953864225', label: 'LinkedIn' },
    { icon: Mail, href: 'mailto:rakshanshetty2003@gmail.com', label: 'Email' },
    { icon: Phone, href: 'tel:+919632612163', label: 'Phone' },
  ];

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skill' },
    { name: 'Open Source', href: '#opensource' },
    { name: 'Projects', href: '#projects' },
    { name: 'Freelance', href: '#freelance' },
    { name: 'Experience', href: '#experience' },
    { name: 'Education', href: '#education' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const section = document.querySelector(href);
    if (section) {
      const offset = section.getBoundingClientRect().top + window.scrollY - 64;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-[var(--bg-canvas)] text-[var(--fg-secondary)] border-t border-[var(--bg-recessed)] py-10 px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-10">
          {/* About Section */}
          <div className="md:col-span-2">
            <h3 className="flex items-center gap-2.5 text-base font-semibold text-[var(--fg-primary)] tracking-tight mb-2">
              <Logo size={28} title="" />
              Rakshan Shetty
            </h3>
            <p className="text-sm text-[var(--fg-secondary)] leading-relaxed max-w-md mb-4">
              Full Stack Developer & AI/ML Engineer focused on building fast, modern software solutions and intelligent systems.
            </p>
            <div className="space-y-1.5 text-xs text-[var(--fg-secondary)]">
              <div className="flex items-center space-x-2">
                <MapPin className="w-3.5 h-3.5 text-[var(--fg-muted)]" />
                <span>Bantwal, India</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-3.5 h-3.5 text-[var(--fg-muted)]" />
                <span>rakshanshetty2003@gmail.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3">
              Navigation
            </h4>
            <ul className="space-y-2 text-sm">
              {quickLinks.slice(0, 4).map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="hover:text-[var(--fg-primary)] transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect & Social */}
          <div>
            <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3">
              Connect
            </h4>
            <div className="flex space-x-2">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="p-2 rounded-md bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)] text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-6 border-t border-[var(--bg-recessed)] flex flex-col sm:flex-row justify-between items-center t-eyebrow normal-case tracking-normal text-[var(--fg-secondary)] gap-4">
          <p>© {currentYear} Rakshan Shetty. All rights reserved.</p>
          <div className="flex space-x-4">
            <span>Built with React & Vite</span>
          </div>
        </div>
      </div>

      {/* Scroll to top.
          It sits at bottom-24, not bottom-6: the chatbot launcher is fixed at
          bottom-6 right-6 with z-50, so this button used to render underneath
          it and every click landed on the chatbot instead. There was also a
          third button here that only scrolled to the chatbot — redundant next
          to the launcher itself, so it is gone. */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-md)] transition-colors hover:bg-[var(--bg-hover)] cursor-pointer"
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </footer>
  );
};

export default Footer;
